// Tabblad Verloop in de taaklade (ontwerp "Verloop van een taak", versie 4): van oud naar nieuw, het nieuwste
// onderaan en het schrijfveld vast eronder; de lijst scrolt zelf en opent onderaan. Leest u verder naar boven, dan
// springt ze niet terug bij een nieuwe regel ("Nieuwe regels" brengt u onderaan); uw eigen update wel.
// Een update is tekst die in de app blijft, met het statuslabel. Een ander label kiezen zet het in Planner langs de
// gewone schrijfweg, met dezelfde rechten en licentie als de sectie Labels, en als enige statuslabel: de andere labels
// met een betekenis gaan eraf, labels op "negeren" blijven. Wie het label niet mag of kan zetten, schrijft wel en ziet
// waar het label wél gewijzigd wordt. Updates blijven altijd staan.
import { useQueryClient } from "@tanstack/react-query";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { historyApi, useVerloop, type HistoryEvent, type Verloop } from "../lib/api-history";
import { catOf, labelColor, labelFor } from "../lib/board";
import { setLabelOps } from "../lib/cleanup";
import { useTaskOps } from "../lib/hooks";
import { Icon, type IconName } from "../lib/icons";
import { addDays, meaningOf } from "../lib/planning";
import type { PlanningData, Task } from "../lib/types";
import { useToast } from "../shell/Toast";
import { Av } from "./Avatar";

const ICONS = new Set(["tag", "plus", "cal", "check", "flag", "board", "pen", "users", "arrow", "link", "clip", "x", "gauge", "clock", "planning", "chat", "fase", "diamond"]);
const DAYS = ["zondag", "maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag"];
type Filter = "alles" | "updates" | "wijzigingen";
const KIND: Record<HistoryEvent["kind"], Filter | null> = { update: "updates", app: "wijzigingen", planner: "wijzigingen", mark: null };

const timeOf = (at: string | null) => (at ? new Date(at).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Brussels" }) : "");
const dm = (d: string, today: string) => d.slice(8, 10) + "/" + d.slice(5, 7) + (d.slice(0, 4) === today.slice(0, 4) ? "" : "/" + d.slice(0, 4));
const weekday = (d: string) => DAYS[new Date(d + "T12:00:00Z").getUTCDay()];
function dayTitle(d: string, today: string) {
  if (d === today) return <>Vandaag <span>{dm(d, today)}</span></>;
  if (d === addDays(today, -1)) return <>Gisteren <span>{dm(d, today)}</span></>;
  return <>{weekday(d)} {dm(d, today)}</>;
}
const Chip = ({ name }: { name: string }) => <span className="p-lbc"><i style={{ background: labelColor(name) }} />{name}</span>;

/** De statuslabels van het plan en het huidige statuslabel van de taak. */
function statusOf(task: Task, data: PlanningData) {
  const labels = data.labels.filter((l) => l.projectId === task.projectId && meaningOf(l.name) !== "negeren").sort((a, b) => a.name.localeCompare(b.name));
  const name = labelFor(task) ?? task.labels.find((l) => meaningOf(l.name) !== "negeren")?.name ?? null;
  return { labels, name, id: task.labels.find((l) => l.name === name)?.id ?? null };
}

export function TaskHistory({ task, data, canLabel, write, owners, plannerUrl }: {
  task: Task; data: PlanningData;
  /** het label mag vanuit de app: planner of eigenaar, met een Project-licentie (zoals de sectie Labels) */
  canLabel: boolean;
  write: boolean;
  owners: string;
  plannerUrl: string;
}) {
  const { data: v, isError } = useVerloop(task.id);
  const ops = useTaskOps();
  const qc = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>("alles");
  const [text, setText] = useState("");
  const [pick, setPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const stick = useRef(true); // onderaan houden: bij openen, na een filter en na een eigen update
  const seen = useRef(0);

  const events = v?.events || [];
  const shown = events.filter((e) => filter === "alles" || e.kind === "mark" || KIND[e.kind] === filter);
  const count = shown.length;
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    if (stick.current) { el.scrollTop = el.scrollHeight; setFresh(false); } else if (count > seen.current && seen.current > 0) setFresh(true);
    seen.current = count;
  }, [count, filter]);
  const onScroll = () => {
    const el = box.current;
    if (!el) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    if (stick.current) setFresh(false);
  };
  const toBottom = () => { stick.current = true; const el = box.current; if (el) el.scrollTop = el.scrollHeight; setFresh(false); };

  const st = statusOf(task, data);
  const chosenId = canLabel ? pick ?? st.id : st.id;
  const chosen = st.labels.find((l) => l.id === chosenId) || null;
  const change = canLabel && !!chosen && chosen.id !== st.id;
  const effect = (() => {
    if (!change || !chosen) return "";
    const to = meaningOf(chosen.name), now = catOf(task);
    if (to === now) return "";
    return to === "geparkeerd" ? "; de taak gaat naar Geparkeerd" : to === "afsluiten" ? "; de taak gaat naar Afsluiten" : "; de taak wordt weer actief";
  })();

  const post = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    let stage: "label" | "update" = "label";
    try {
      let changeId: number | null = null;
      if (change && chosen) {
        changeId = (await ops.run(task, setLabelOps(task, chosen), "Update geplaatst · label in Planner nu " + chosen.name)).changeId;
      }
      stage = "update";
      const r = await historyApi.post(task.id, { text: t, label: change && chosen ? chosen.name : st.name || "", labelFrom: change ? st.name : null, changeId });
      qc.setQueryData<Verloop>(["verloop", task.id], (old) => (old ? { ...old, events: [...old.events, r.event], latest: r.event } : old));
      qc.invalidateQueries({ queryKey: ["verloop", task.id] });
      if (!change) toast.flash("Update geplaatst");
      stick.current = true;
      setText(""); setPick(null);
    } catch (e) {
      // een geweigerde labelwijziging meldt useTaskOps zelf; dan is de update ook niet geplaatst
      if (stage === "update") toast.flash("Update niet geplaatst: " + (e instanceof Error ? e.message : "onbekende fout"), { error: true });
    } finally {
      setBusy(false);
    }
  };

  const counts: Record<Filter, number> = { alles: 0, updates: 0, wijzigingen: 0 };
  for (const e of events) { const k = KIND[e.kind]; if (k) { counts[k]++; counts.alles++; } }

  const rows: ReactNode[] = [];
  let day = "";
  shown.forEach((e, i) => {
    if (e.kind === "mark") { rows.push(<div key={e.id} className="p-vl-mark">De app houdt het verloop bij sinds {weekday(e.day)} {dm(e.day, data.today)}</div>); day = ""; return; }
    if (e.day !== day) { day = e.day; rows.push(<div key={"d" + e.day + i} className="p-vl-day">{dayTitle(e.day, data.today)}</div>); }
    const next = shown[i + 1], last = !next || next.kind === "mark" || next.day !== e.day;
    if (e.kind === "update") {
      const p = e.personId ? data.people.find((x) => x.id === e.personId) : null;
      rows.push(
        <div key={e.id} className={"p-vl-ev" + (last ? " last" : "")}>
          <Av id={e.personId} init={p?.initials || (e.who || "?").slice(0, 2).toUpperCase()} cls="p-av p-vl-av" />
          <div className="p-vl-bd"><div className="p-vl-up">
            <div className="p-vl-uph"><b>{e.who || "Iemand"}</b>{e.label && <Chip name={e.label} />}{e.labelFrom && <span className="p-vl-from">van {e.labelFrom}</span>}<span className="p-vl-tm">{timeOf(e.at)}</span></div>
            <p>{e.text}</p>
          </div></div>
        </div>,
      );
    } else {
      rows.push(
        <div key={e.id} className={"p-vl-ev sys" + (last ? " last" : "")}>
          <span className="p-vl-ic"><Icon name={(ICONS.has(e.icon) ? e.icon : "info") as IconName} size={14} /></span>
          <div className="p-vl-bd"><div className="p-vl-ln">{e.who && <b>{e.who} </b>}{e.text}</div><div className="p-vl-mt">{e.src}{e.at ? " · " + timeOf(e.at) : ""}</div></div>
        </div>,
      );
    }
  });

  return (
    <>
      <div className="p-quick" role="group" aria-label="Filter">
        {([["alles", "Alles"], ["updates", "Updates"], ["wijzigingen", "Wijzigingen"]] as [Filter, string][]).map(([k, t]) => (
          <button key={k} type="button" className={"p-tog p-tog-s" + (filter === k ? " on" : "")} aria-pressed={filter === k} onClick={() => { stick.current = true; setFilter(k); }}>
            {t} <b className="p-vl-n">{counts[k]}</b>
          </button>
        ))}
      </div>
      <div className="p-vl-wrap">
        <div ref={box} className="p-vl-scroll" tabIndex={0} aria-label="Verloop, van oud naar nieuw" onScroll={onScroll}>
          <div className="p-vl-list">
            {!v ? <p className="p-kd-note">{isError ? "Het verloop is nu niet te lezen." : "Verloop laden…"}</p>
              : rows.length ? rows : <p className="p-kd-note">{filter === "alles" ? "Nog niets in het verloop." : "Niets in deze selectie."}</p>}
          </div>
        </div>
        {fresh && <button type="button" className="p-tog p-vl-fresh" onClick={toBottom}><Icon name="down" size={13} />Nieuwe regels</button>}
      </div>
      <form className="p-vl-comp" onSubmit={(e) => { e.preventDefault(); post(); }}>
        <label className="ex-label" htmlFor="vl-text">Stand van zaken</label>
        <textarea id="vl-text" className="ex-input" rows={3} maxLength={2000} placeholder="Wat is er gebeurd, waar wacht het op, wat is de volgende stap?" value={text}
          onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); post(); } }} />
        {st.labels.length > 0 && (
          <div className="p-vl-labels"><span className="t-mute" style={{ fontSize: 13 }}>Label</span>
            {st.labels.map((l) => (
              <button key={l.id} type="button" className={"p-tog p-tog-s" + (chosenId === l.id ? " on" : "")} aria-pressed={chosenId === l.id} disabled={!canLabel || busy}
                onClick={() => setPick(l.id)}><span className="p-lbl"><i style={{ background: labelColor(l.name) }} />{l.name}</span></button>
            ))}
          </div>
        )}
        <div className="p-vl-foot">
          <span>{change && chosen ? <b className="p-vl-chg">Zet het label in Planner van {st.name || "geen label"} naar {chosen.name}{effect}.</b>
            : canLabel ? <>Het label blijft {st.name ? <b>{st.name}</b> : "leeg"}. Ctrl+Enter plaatst.</>
            : !write ? <>Zonder Project-licentie zet u het label in <a className="ex-link" href={plannerUrl} target="_blank" rel="noreferrer">Planner zelf</a>; de update bewaart de app.</>
            : <>Het label wijzigt {owners || "de eigenaar"} of de planner; uw update bewaart de app.</>}</span>
          <button type="submit" className="ex-btn ex-btn--primary" disabled={!text.trim() || busy}>Plaatsen</button>
        </div>
      </form>
    </>
  );
}
